/**
 * Sign in (D8). Four actions and nothing else: create a passkey, sign in with a passkey,
 * ask for an email, or type the code that email carries. Passkeys are the first offer
 * because they are the only one that works inside an installed app without leaving it;
 * the code is the second, for the same reason (item 79). Guest play needs none of them;
 * this is only for keeping the world when the browser is not.
 *
 * It keeps its own open/closed state rather than joining the game store, because
 * signing in is not part of the simulation and nothing else needs to read it.
 */
import { useEffect, useState } from 'preact/hooks';
import {
  closeSignIn,
  isSignInOpen,
  onSignInOpenChange,
  openSignIn,
  takePendingResult,
} from '../game/signin-bus.ts';
import {
  consumeSignInOutcome,
  currentAccount,
  hasSignedIn,
  registerPasskey,
  requestMagicLink,
  signInWithCode,
  signInWithPasskey,
  signOut,
  type Account,
  type SignInResult,
} from '../game/sync.ts';
import {
  SIGNIN_CODE_ACTION,
  SIGNIN_CODE_LABEL,
  SIGNIN_CODE_NOTE,
  SIGNIN_EMAIL_ACTION,
  signinSessionEndedNote,
  REASON_NEEDS_CODE,
  REASON_NEEDS_EMAIL,
  REASON_WAIT,
} from '../game/copy.ts';
import { Button } from '../ui/Button.tsx';
import { Sheet } from '../ui/Sheet.tsx';

const LABEL = {
  font: '400 12px/1.4 var(--font-ui)',
  color: 'var(--ink-2)',
} as const;

const INPUT = {
  height: 44,
  width: '100%',
  padding: '0 12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--line)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  font: '400 14px/1 var(--font-ui)',
  boxSizing: 'border-box',
} as const;

// The open/closed flag and any pending message live in `game/signin-bus.ts`, shared with
// the game store so it can pop the sheet up on its own (a session ending behind a 401).
export { openSignIn };

function useSignInOpen(): boolean {
  const [value, setValue] = useState(isSignInOpen());
  useEffect(() => onSignInOpenChange(setValue), []);
  return value;
}

/**
 * The account this browser is signed in as, refreshed whenever the sheet opens.
 * Also picks up the message a magic-link redirect left in the address bar.
 */
export function useAccount(): { account: Account | null; refresh: () => void } {
  const [account, setAccount] = useState<Account | null>(null);
  const [nonce, setNonce] = useState(0);
  const isOpen = useSignInOpen();
  useEffect(() => {
    let live = true;
    void currentAccount().then((next) => {
      if (live) setAccount(next);
    });
    return () => {
      live = false;
    };
  }, [isOpen, nonce]);
  return { account, refresh: () => setNonce((n) => n + 1) };
}

/**
 * Sign in. Rendered by the idle first run (D266) and opened from settings; `onSignedIn`
 * opens the account's world once the player is in.
 */
export function SignInScreen({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const isOpen = useSignInOpen();
  const { account, refresh } = useAccount();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SignInResult | null>(null);

  // Something opened the sheet on its own, with a message already chosen (e.g. the
  // session-ended notice). Show it once.
  useEffect(() => {
    if (!isOpen) return;
    const pending = takePendingResult();
    if (pending) setResult(pending);
  }, [isOpen]);

  // A magic link comes back to the app as a redirect, so the outcome is in the
  // address bar rather than in a response. Read once, on load.
  useEffect(() => {
    const outcome = consumeSignInOutcome();
    if (outcome) {
      setResult(outcome);
      openSignIn();
    }
  }, []);

  if (!isOpen) return null;

  const run = async (action: () => Promise<SignInResult>) => {
    setBusy(true);
    const outcome = await action();
    setResult(outcome);
    setBusy(false);
    refresh();
    // The session now points at a different account, and under D1 the world belongs to the
    // session. Re-open so the screens are looking at the right world rather than the old one,
    // and leave onboarding where the account turns out to have one already (item 78).
    if (outcome.ok) await onSignedIn();
  };

  const guest = account?.isGuest !== false;
  return (
    <Sheet
      title="Sign in"
      screenLabel="Sign in"
      testId="signin-sheet"
      zIndex={50}
      onClose={closeSignIn}
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={LABEL}>
          {account === null
            ? // D190: a device that has used a passkey or an email is not a new player and
              // is not given a guest world, so say where the world actually is.
              hasSignedIn()
              ? signinSessionEndedNote()
              : 'Playing offline. The world is stored in this browser only.'
            : guest
              ? 'Playing as a guest. The world lives in this browser, so clearing its data loses it. Add a passkey or an email to keep it.'
              : `Signed in as ${account.email ?? account.displayName}. Your world follows the account.`}
        </div>

        {result && (
          <div
            style={{
              font: '400 13px/1.45 var(--font-ui)',
              color: result.ok ? 'var(--ink)' : 'var(--red)',
              background: 'var(--surface-2, transparent)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius-md)',
              padding: 10,
            }}
          >
            {result.message}
            {result.link ? (
              <div style={{ marginTop: 8 }}>
                <Button onClick={() => (location.href = result.link as string)}>
                  Open the link
                </Button>
              </div>
            ) : null}
            {result.code ? (
              <div style={{ ...LABEL, marginTop: 6 }}>{`The code is ${result.code}.`}</div>
            ) : null}
            {result.merged && result.archivedWorldId ? (
              <div style={{ ...LABEL, marginTop: 6 }}>
                The guest world is kept as history, not deleted.
              </div>
            ) : null}
          </div>
        )}

        <Button
          variant="primary"
          full
          disabled={busy}
          explain={REASON_WAIT}
          onClick={() => void run(() => registerPasskey())}
        >
          {guest ? 'Create a passkey' : 'Add a passkey to this account'}
        </Button>

        {guest && (
          <Button
            variant="secondary"
            full
            disabled={busy}
            explain={REASON_WAIT}
            onClick={() => void run(() => signInWithPasskey())}
          >
            Sign in with a passkey
          </Button>
        )}

        <div style={{ display: 'grid', gap: 6 }}>
          <label style={LABEL} for="signin-email">
            Or use your email
          </label>
          <input
            id="signin-email"
            type="email"
            autocomplete="email"
            placeholder="you@example.com"
            value={email}
            style={INPUT}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          />
          <Button
            variant="secondary"
            full
            disabled={busy || email.trim().length < 3}
            explain={busy ? REASON_WAIT : REASON_NEEDS_EMAIL}
            onClick={() => void run(() => requestMagicLink(email.trim()))}
          >
            {SIGNIN_EMAIL_ACTION}
          </Button>
        </div>

        {/* Item 79: a link opens the browser, which is the wrong place when the game is
            installed, so the same email carries a code that signs in from here. */}
        <div style={{ display: 'grid', gap: 6 }}>
          <label style={LABEL} for="signin-code">
            {SIGNIN_CODE_LABEL}
          </label>
          <input
            id="signin-code"
            type="text"
            inputMode="numeric"
            autocomplete="one-time-code"
            placeholder="123456"
            maxLength={6}
            value={code}
            style={INPUT}
            onInput={(e) => setCode((e.target as HTMLInputElement).value.replace(/\D/g, ''))}
          />
          <Button
            variant="secondary"
            full
            disabled={busy || email.trim().length < 3 || code.length !== 6}
            explain={
              busy ? REASON_WAIT : email.trim().length < 3 ? REASON_NEEDS_EMAIL : REASON_NEEDS_CODE
            }
            onClick={() => void run(() => signInWithCode(email.trim(), code))}
          >
            {SIGNIN_CODE_ACTION}
          </Button>
          <div style={LABEL}>{SIGNIN_CODE_NOTE}</div>
        </div>

        {account && !guest && (
          <Button
            variant="danger"
            full
            disabled={busy}
            explain={REASON_WAIT}
            onClick={() =>
              void run(async () => {
                await signOut();
                return { ok: true, message: 'Signed out. This browser is a guest again.' };
              })
            }
          >
            Sign out
          </Button>
        )}

        <div style={LABEL}>
          If the account you sign into already has a world, that world is kept and the one on this
          device is put away.
        </div>
      </div>
    </Sheet>
  );
}
